import axios, { type AxiosError, type AxiosResponse, isAxiosError } from 'axios';
import { anyPass, evolve, not, pathEq, when } from 'ramda';

import { RETRYABLE_HTTP_STATUS_CODES } from '@framework/constants/enums/http';
import { camelizeKeys, decamelizeKeys } from '@framework/utils/camelize.utils';

import { type AxiosRequestConfig, type InternalAxiosRequestConfig } from '@type/api';

export const getApiErrorsData = (error: unknown): { errors?: any[] | string; status?: number } => {
    if (!axios.isAxiosError(error)) {
        return {
            errors: undefined,
            status: undefined
        };
    }

    const { response } = error;
    const { data, status } = response ?? {};

    return {
        errors: data?.errors,
        status
    };
};

// only check status and data of the response when there is one (if the connection is lost, no response is available)
const isTokenExpired = (response: AxiosResponse) =>
    response?.status === 401 || response?.data?.code === 'authentication:token-expired';
const isUserUnknown = (response: AxiosResponse) =>
    response?.status === 400 && response?.data?.code === 'unknown-user-id';

// exclude certain keys from camelization
const regexUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const excludeKeys = (key: string, convert: (key: string) => string): string =>
    regexUUID.test(key) ? key : convert(key);

// only camelize keys when response content matches
export const shouldCamelizeResponse: (response: AxiosResponse) => boolean = pathEq(
    'application/vnd.sherpany.boardroom+json',
    ['headers', 'content-type']
);
export const camelizeResponse = when<AxiosResponse, AxiosResponse>(
    shouldCamelizeResponse,
    (response) => camelizeKeys(response, excludeKeys) as AxiosResponse
);
const camelizeAxiosError = (error: AxiosError) => {
    if (error.response) {
        // eslint-disable-next-line no-param-reassign
        error.response = camelizeResponse(error.response);
    }
    return error;
};

// only decamelize when data is not from form
const shouldDecamelizeRequest = (config: InternalAxiosRequestConfig): boolean =>
    not(config.data instanceof FormData || config?.decamelize === false);
export const decamelizeRequest = when<InternalAxiosRequestConfig, InternalAxiosRequestConfig>(
    shouldDecamelizeRequest,
    evolve({ data: decamelizeKeys, params: decamelizeKeys })
);

export const handleErrorResponse = (error: AxiosError) => {
    const { noRedirect } = error.config as AxiosRequestConfig;

    if (error.response && !noRedirect) {
        // redirect to logout screen if user is unauthorized
        const shouldRedirectToLoginPage = anyPass([isTokenExpired, isUserUnknown]);
        if (shouldRedirectToLoginPage(error.response)) {
            window.location.href = `/?next=${window.location.pathname}${window.location.search}${window.location.hash}`;
            return Promise.reject(error);
        }
    }
    return Promise.reject(camelizeAxiosError(error));
};

export const isRetryableError = (
    error: unknown,
    retryableStatusCodes = RETRYABLE_HTTP_STATUS_CODES
) => {
    if (isAxiosError(error)) {
        // Network errors (no response received)
        if (!error.response) {
            return true;
        }

        if (error.response.status && retryableStatusCodes.includes(error.response.status)) {
            return true;
        }
    }
    return false;
};
